import type { ApiFetcher } from "@sukha/query/apiFetcher";
import { isSignOutApiBlocked } from "@sukha/query/signOutGate";

import { getApiToken } from "@/lib/api/authToken";
import { getApiBaseUrl } from "@/lib/utils";

export const SESSION_REQUEST_TIMEOUT_MS = 60_000;

export function isSessionEndpoint(endpoint: string): boolean {
  return endpoint.startsWith("/api/v1/sessions/");
}

function createTimeoutSignal(timeoutMs: number): {
  signal: AbortSignal;
  cleanup: () => void;
} {
  if (typeof AbortSignal.timeout === "function") {
    return {
      signal: AbortSignal.timeout(timeoutMs),
      cleanup: () => {},
    };
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort(new DOMException("Request timed out", "TimeoutError"));
  }, timeoutMs);

  return {
    signal: controller.signal,
    cleanup: () => window.clearTimeout(timeoutId),
  };
}

function combineSignals(
  primarySignal: AbortSignal,
  secondarySignal?: AbortSignal | null,
): AbortSignal {
  if (!secondarySignal) return primarySignal;

  if (typeof AbortSignal.any === "function") {
    return AbortSignal.any([primarySignal, secondarySignal]);
  }

  const controller = new AbortController();

  const abortFrom = (signal: AbortSignal): void => {
    controller.abort(signal.reason);
  };

  if (primarySignal.aborted) {
    abortFrom(primarySignal);
    return controller.signal;
  }

  if (secondarySignal.aborted) {
    abortFrom(secondarySignal);
    return controller.signal;
  }

  const onPrimaryAbort = (): void => abortFrom(primarySignal);
  const onSecondaryAbort = (): void => abortFrom(secondarySignal);

  primarySignal.addEventListener("abort", onPrimaryAbort, { once: true });
  secondarySignal.addEventListener("abort", onSecondaryAbort, { once: true });

  return controller.signal;
}

/**
 * Cookie-authenticated fetcher for web app and Document PiP windows.
 */
export function createWebApiFetcher(): ApiFetcher {
  return async <T,>(endpoint: string, init?: RequestInit): Promise<T> => {
    if (isSignOutApiBlocked()) {
      throw new DOMException("Sign-out in progress", "AbortError");
    }

    const baseUrl = getApiBaseUrl(endpoint);
    const shouldApplyTimeout = isSessionEndpoint(endpoint);
    const timeout = shouldApplyTimeout
      ? createTimeoutSignal(SESSION_REQUEST_TIMEOUT_MS)
      : null;

    /*
     * The Bearer token, for the same reason `apiRequest` attaches one (TX-4856).
     *
     * This fetcher is the one `@sukha/query` uses, which is most of the app's
     * traffic — the dashboard alone drives a dozen hooks through it. Fixing
     * only `apiRequest` left every one of those calls on cookie auth, and an
     * e2e sweep of four pages found 14 requests going out with no
     * `Authorization` header at all. Same-origin they still worked, which is
     * precisely why nothing had noticed.
     */
    const send = async (bearer: string | null): Promise<Response> =>
      fetch(`${baseUrl}${endpoint}`, {
        ...init,
        headers: {
          "Content-Type": "application/json",
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
          ...(init?.headers as Record<string, string>),
        },
        ...(timeout && {
          signal: combineSignals(timeout.signal, init?.signal),
        }),
      });

    try {
      const token = await getApiToken();
      let response = await send(token);

      // One refresh-and-retry, on 401 only, and only if the fresh token
      // differs — the same narrow rule `apiRequest` uses, for the same reason:
      // a token lives about a minute, and an idle tab must not read as a
      // sign-out.
      if (response.status === 401 && token) {
        const refreshed = await getApiToken({ skipCache: true });
        if (refreshed && refreshed !== token) {
          response = await send(refreshed);
        }
      }

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({
          error: `HTTP ${response.status}: ${response.statusText}`,
        }))) as { error?: string; message?: string; code?: string };
        const err = new Error(
          errorData.error || errorData.message || "API request failed",
        ) as Error & { code?: string; status?: number };
        if (errorData.code) {
          err.code = errorData.code;
        }
        err.status = response.status;
        throw err;
      }

      return response.json() as Promise<T>;
    } finally {
      timeout?.cleanup();
    }
  };
}
